import React, { PropsWithChildren, useEffect, useMemo, useRef } from 'react'
import { observer } from 'mobx-react'

import { LogEventCompressionMode, StatsigClient } from '@statsig/js-client'
import { StatsigProvider, StatsigUser } from '@statsig/react-bindings'
import { runStatsigTriggeredSessionReplay, startRecording, stopRecording } from '@statsig/session-replay'

import { useShopStores } from 'shop/hooks/use-store'

import { checkKeyInCookiesConsents } from 'utils/cookiesConsents.utils'
import { isProduction } from 'utils/env.utils'

import { STATSIG_SESSION_REPLAY_CONSENT } from '@elo-kit/constants/cookiesConsents.constants'

type Props = PropsWithChildren & {
  readonly clientSdkKey: string
  readonly user: StatsigUser
  readonly values: string
}

export const BootstrappedStatsigProvider = observer(
  ({ clientSdkKey, user, values, children }: Props): React.ReactNode => {
    const { experimentsStore, cookiesConsentsStore, sellerStore } = useShopStores()

    const clientRef = useRef<StatsigClient | null>()

    const isAllowedSessionReplay = checkKeyInCookiesConsents(
      STATSIG_SESSION_REPLAY_CONSENT,
      cookiesConsentsStore.list,
      undefined,
      sellerStore.item.username
    )

    const client = useMemo(() => {
      if (clientRef.current) {
        return clientRef.current
      }

      const client = new StatsigClient(clientSdkKey, user, {
        logEventCompressionMode: isProduction() ? LogEventCompressionMode.Enabled : LogEventCompressionMode.Disabled,
      })

      clientRef.current = client
      client.dataAdapter.setData(values)
      client.initializeSync()

      runStatsigTriggeredSessionReplay(client, { rrwebConfig: { maskAllInputs: true } })

      experimentsStore.setClient(client)

      return client
    }, [clientSdkKey, user, values])

    useEffect(() => {
      if (!client) {
        return
      }

      if (isAllowedSessionReplay) {
        startRecording(clientSdkKey)
      } else {
        stopRecording(clientSdkKey)
      }
    }, [client, cookiesConsentsStore.acceptedCookieConsents])

    if (client) {
      return <StatsigProvider client={client}>{children}</StatsigProvider>
    }

    return children
  }
)
